<template>
  <div class="registration-vehiclels">
    <el-form>

      <el-row>
        <el-col>
          <div class="page-header">
            <h1 class="h1 page-title">WebParking Software</h1>
            <h2 class="h2 page-subtitle">Vehicle registration form</h2>
          </div>
        </el-col>
      </el-row>

      <el-row>
        <el-col>
          <el-card class="box-card" v-loading.fullscreen.lock="loading || submitting">
            <div slot="header" class="card-header">Vehicle form</div>
            <div class="card-body">
              <div class="vehicle-subform">
                  <el-col>
                    <el-form ref="vehicleForm" :rules="validationRules" :model="vehicle">

                      <el-form-item label="Select Make" prop="makeId">
                        <el-select filterable clearable
                          v-model="vehicle.makeId" @change="vehicleMakeChange" >
                          <el-option
                            v-for="make in makesList"
                            :key="make.id"
                            :label="make.name"
                            :value="make.id"
                          />
                        </el-select>
                      </el-form-item>

                      <el-form-item label="Select Model" prop="model">
                        <el-select filterable clearable
                          v-model="vehicle.model" :disabled="!vehicle.makeId" >
                          <el-option
                            v-for="model in modelsById"
                            :key="model.id"
                            :label="model.name" 
                            :value="model.name" 
                          />
                        </el-select>
                      </el-form-item>

                      <el-form-item v-if="fullData" label="Vehicle year" prop="year">
                        <el-input v-model="vehicle.year" />
                      </el-form-item>

                      <el-form-item v-if="fullData" label="Vehicle color">
                        <el-input v-model="vehicle.color" />
                      </el-form-item>

                      <el-form-item label="Vehicle license plate" prop="licensePlate">
                        <el-input
                          ref="licenseInput"
                          :value="vehicle.licensePlate"
                          @input="onLicenseInput"
                        />
                      </el-form-item>

                      <el-form-item v-if="fullData" label="VIN code" prop="vin">
                        <el-input
                          ref="vinInput"
                          :value="vehicle.vin"
                          @input="onVinInput"
                        />
                      </el-form-item>


                      <el-form-item label="Select vehicle country" prop="countryId">
                        <el-select filterable clearable remote
                          v-model="vehicle.countryId" @change="countryChange" >
                          <el-option 
                            v-for="(country, index) in countries" 
                            :key="index" 
                            :label="country.name"
                            :value="country.id"
                          />
                        </el-select>
                      </el-form-item>

                      <el-form-item label="Select State or Province" prop="provinceId">
                        <el-select filterable clearable remote allow-create
                          v-model="vehicle.provinceId" :disabled="!vehicle.countryId" >
                          <el-option 
                            v-for="province in provinces"
                            :key="province.id" 
                            :label="province.name"
                            :value="province.id"
                          />
                        </el-select>
                      </el-form-item>

                      <el-form-item v-if="vehicleDocs" label="Upload Vehicle Insurance">
                        <el-upload
                          class="dropzone"
                          ref="dropzoneInsurance"
                          drag
                          multiple
                          :auto-upload="false"
                          :action="`test`"
                          :http-request="submitInsuranceFile"
                          :file-list="vehicle.insurance"
                          :limit="1"
                          :on-remove="deleteInsuranceFile"
                        >
                          <i class="el-icon-upload"></i>
                          <div class="el-upload__text">Drop file here or <em>click to upload</em></div>
                          <div class="el-upload__tip" slot="tip">jpg/png files with a size less than 500kb</div>
                        </el-upload>
                      </el-form-item>

                      <el-form-item v-if="vehicleDocs" label="Upload DMV Registration">
                        <el-upload
                          class="dropzone"
                          ref="dropzoneDmv"
                          drag
                          multiple
                          :auto-upload="false"
                          :action="`test`"
                          :http-request="submitDmvFile"
                          :file-list="vehicle.dvmReg"
                          :limit="1"
                          :on-remove="deleteDmvFile"
                        >
                          <i class="el-icon-upload"></i>
                          <div class="el-upload__text">Drop file here or <em>click to upload</em></div>
                          <div class="el-upload__tip" slot="tip">jpg/png files with a size less than 500kb</div>
                        </el-upload>
                      </el-form-item>

                    </el-form>
                  </el-col>
              </div>

            </div>
          </el-card>
        </el-col>

        <el-col>
          <el-button type="primary" @click="$router.go(-1)" icon="el-icon-arrow-left" :disabled="submitting">Back</el-button>
          <el-button type="primary" :loading="submitting" :disabled="submitting" @click="goNextPage()">Continue<i class="el-icon-arrow-right"></i></el-button>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import CheckCode from '../mixins/check-code'
import ComputedStorage from '../mixins/computed-storage'
import VehicleMakeService from '../services/vehicle-make-service'
import VehicleModelService from '../services/vehicle-model-service'
import CountriesService from '../services/countries-service'
import ProvinceService from '../services/provinces-service'
import VehicleService from '../services/vehicle-service'

const ruleRequired = { required: true, message: 'This field cannot be blank', trigger: 'change' };

export default {
  props: [ 'num' ],
  mixins: [ CheckCode, ComputedStorage ],
  data() {
    return {
      code: null,
      fullData: true,
      vehicleDocs: true,
      licenseExists: false ,
      countries: [],
      provinces: [],
      loading: true,
      submitting: false,
      makesList: null,
      modelsList: null,
      vehicle: {
        id: null,
        makeId: null,
        model: null,
        year: null,
        color: null,
        licensePlate: null,
        vin: null,
        countryId: null,
        provinceId: null,
        insurance: [],
        dvmReg:[]
      },

      validationRules: {
        makeId: [ ruleRequired ],
        model: [ ruleRequired ],
        year: [ 
          { required: true, message: 'This field cannot be blank', trigger: 'blur' }, 
          { validator: this.checkYear, trigger: 'blur' }
        ],
        licensePlate: [
          { validator: this.checkLicense, trigger: 'blur' }
        ],
        vin: [
          { validator: this.checkLicense, trigger: 'blur' },
          { min: 10, message: 'VIN code should contain at least 10 characters', trigger: 'blur' }
        ],
        countryId: [ ruleRequired ],
        provinceId: [ ruleRequired ]
      }
    };
  },

  computed: {
    modelsById() {
      if (!this.vehicle.makeId || !this.modelsList) return []
      return this.modelsList.filter(model => model.make !== null && model.make.id === this.vehicle.makeId)
    },

    selectedModelId() {
      if (!this.vehicle.model || !this.modelsList) return this.$store.state.vehicle.modelId
      for (let model of this.modelsList) {
        if (model.name !== this.vehicle.model) continue
        return model.id
      }
      return null
    },

    makeName() {
      if (!this.makesList) return false
      for(let make of this.makesList) {
        if (this.vehicle.makeId !== make.id) continue
        return make.name
      }
      return false
    },

    provinceName() {
      if(!this.provinces) return false
      for (let prov in this.provinces) {
        if (prov.id !== this.vehicle.provinceId) continue
        return prov.name
      }
      return false
    }
  },
  
  methods: {
    sanitizeVehicleInput(value) {
      return value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
    },
    onLicenseInput(event) {
      const sanitized = this.sanitizeVehicleInput(event)
      this.vehicle.licensePlate = sanitized
      this.$nextTick(() => {
        this.$refs.licenseInput.$el.querySelector('input').value = sanitized
      })
    },
    onVinInput(event) {
      const sanitized = this.sanitizeVehicleInput(event)
      this.vehicle.vin = sanitized
      this.$nextTick(() => {
        this.$refs.vinInput.$el.querySelector('input').value = sanitized
      })
    },
    goNextPage() {
      if (this.submitting) return
      this.submitting = true

      const that = this
      this.$refs.vehicleForm.validate(valid => {
        if (!valid) {
          that.submitting = false
          return
        }
        try {
          that.$store.commit('registration/setVehicleData', { ...that.vehicle, make: that.makeName, provinceName: that.provinceName} )

          if (typeof that.vehicle.provinceId === 'string') {
            that.createProvince()
          }
          
          // updating vehicle if we already have a data
          let data = that.getValidVehicleData(),
            vehicleRequest = (!that.vehicleId ? 
              VehicleService.create(data) : 
              VehicleService.update(that.vehicleId, data) )

          vehicleRequest.then(response => {
            that.$store.commit('registration/setVehicleId', response.data.id )
            that.vehicle.id = response.data.id

            if (that.vehicleDocs === true) {
              that.$refs.dropzoneInsurance.submit()
              that.$refs.dropzoneDmv.submit()
            }

            setTimeout(() => {
              let rules = that.code.building.rules,
                consent = that.code.building.consent,
                page = { name: 'GuestRegistrationThanks' }


              if (rules.required || consent.required) {
                let key = rules.required ? 'GuestRegistrationRules' : 'GuestRegistrationConsent'

                page = { name: key, query: { code: that.invitationCode } }
              }

              that.$router.push(page)
            }, 2000)
          }).catch(err => {
            that.submitting = false
            throw new Error('Error in vehicle request: ', err)
          })

        } catch (err) {
          that.submitting = false
          throw new Error('Error in method "setVehicleData": ', err)
        }
      })
    },

    submitInsuranceFile(file) {
      if (this.vehicleDocs === false) {
        return
      }

      let url = `${VehicleService.getFileUploadUrl()}/${this.vehicle.id}/insurance`
      VehicleService.uploadFile(url, file.file, this.code.code).then( response => {
        let data = {
          id: response.data.resource.id,
          file: file.file
        }
        this.$store.commit('registration/setVehicleInsuranceFile', data)
      });
    },

    submitDmvFile(file) {
      if (this.vehicleDocs === false) {
        return
      }

      let url = `${VehicleService.getFileUploadUrl()}/${this.vehicle.id}/dmv`
      VehicleService.uploadFile(url, file.file, this.code.code).then( response => {
        let data = {
          id: response.data.resource.id,
          file: file.file
        }
        this.$store.commit('registration/setVehicleDmvFile', data)
      });
    },

    deleteInsuranceFile() {
      let url = `${VehicleService.getFileDeleteUrl()}/${this.vehicleInsuranceFile.id}/${this.guestId}`
      VehicleService.deleteFileByUrl(url, this.code.code).then( response => {
        if (response.data.deleted){
          this.$store.commit('registration/setVehicleInsuranceFile', {id:null, file:null})
        }
      });
    },

    deleteDmvFile() {
      let url = `${VehicleService.getFileDeleteUrl()}/${this.vehicleDmvFile.id}/${this.guestId}`
      VehicleService.deleteFileByUrl(url, this.code.code).then( response => {
        if (response.data.deleted){
          this.$store.commit('registration/setVehicleDmvFile', {id:null, file:null})
        }
      });
    },

    checkYear(rule, value, callback) {
      if ( value != parseInt(value) ) {
        callback(new Error('Please input only digits'))
      }
      if (+value < 1901) {
        callback(new Error('Year cannot be less than 1901'))
      } else if (+value > new Date().getFullYear()) {
        callback(new Error('Year cannot be greater then current one'))
      } else {
        callback()
      }
    },

    checkLicense(rule, value, callback) {
      if (!this.vehicle.licensePlate && !this.vehicle.vin) {
        callback(new Error('You must fill at least one of the attributes "License Plate" or "VIN code"'))
      } else {
        callback()
      }
    },

    getValidVehicleData () {
      return {
        code: this.invitationCode,
        make_id: this.vehicle.makeId, 
        model_id: this.selectedModelId,
        year: +this.vehicle.year,
        country_id: this.vehicle.countryId,
        province_id: this.vehicle.provinceId,
        color: this.vehicle.color,
        license: this.vehicle.licensePlate,
        vin: this.vehicle.vin,
        guest_id: this.guestId
      }
    },

    countryChange() {
      this.vehicle.provinceId = null

      ProvinceService.getListById(this.vehicle.countryId, this.invitationCode).then(resp => {
        this.provinces = resp.data
      })
    },

    vehicleMakeChange(){
      this.vehicle.model = null
    },
    
    createProvince() {
      let data = {
        country_id: this.vehicle.countryId,
        name: this.vehicle.provinceId,
      }
      ProvinceService.create(data, this.invitationCode).then((resp) => {
        this.vehicle.provinceId = resp.data.id
        console.log(this.vehicle.provinceId, resp.data.id);
      }).catch(err => {
        throw new Error(err)
      })
    },

    checkVehicleFullDataAndDocsRequired(code) {
      if (code && code.building.vehicleFullData === false) {
        this.fullData = false
        this.validationRules.year = null
        this.validationRules.vin = null
      }

      if (code && code.building.requireVehicleDocs === false) {
        this.vehicleDocs = false
      }
    }
  },

  mounted() {
    VehicleMakeService.getList(this.invitationCode).then(resp => {
      this.makesList = resp.data
    })

    VehicleModelService.getList(null, this.invitationCode).then(resp => {
      this.modelsList = resp.data
    })

    CountriesService.getList(this.invitationCode).then(resp => {
      this.countries = resp.data
    }).then(() => {
      if (!this.vehicle.countryId) return false
      ProvinceService.getListById(this.vehicle.countryId, this.invitationCode).then(resp => {
        this.provinces = resp.data
      })
    })

    if (this.vehicleData) {
      this.vehicle = this.vehicleData
    }

    this.vehicle.dvmReg = []
    this.vehicle.insurance = []

    if (this.vehicleDocs === true && this.vehicleInsuranceFile.file !== null) {
      this.vehicle.insurance= [this.vehicleInsuranceFile.file]
    }

    if (this.vehicleDocs === true && this.vehicleDmvFile.file !== null) {
      this.vehicle.dvmReg = [this.vehicleDmvFile.file]
    }

    this.loading = false
  },

  created() {
    this.checkInviteCode(code => {
      this.code = code;
      this.checkVehicleFullDataAndDocsRequired(code);
    });
  }
}
</script>

<style lang="scss">
.el-card__header {
  background-color: teal;
  color: #fff;
}
</style>

<style lang="scss">
.vehicle-subform .el-upload,
.vehicle-subform .el-upload-dragger {
  width: 100%;
}
</style>


<style lang="scss" scoped>
.vehicle-subform {
  text-align: left;
}
.vehicle-subform .el-form-item {
  margin-bottom: 12px;
}
.el-select {
  width: 100%;
}
.vehicle-subform .el-upload__tip {
  margin-top: -20px;
}
</style>
